// src/components/modules/CompletionCertificate.jsx
import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Award,
  Download,
  Share2,
  X,
  CheckCircle,
  Sparkles,
  Calendar,
  BookOpen,
  ArrowRight
} from 'lucide-react';
import html2canvas from 'html2canvas';

const CompletionCertificate = ({ show, onClose, module, userName = 'Learner' }) => {
  const certificateRef = useRef(null);
  const completionDate = new Date().toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  const handleDownload = async () => {
    if (!certificateRef.current) return;

    try {
      const canvas = await html2canvas(certificateRef.current, {
        backgroundColor: '#0f172a',
        scale: 2
      });

      const link = document.createElement('a');
      link.download = `${module.title.replace(/\s+/g, '-')}-certificate.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (error) {
      console.error('Error generating certificate:', error);
    }
  };

  const handleShare = async () => {
    const text = `I just completed "${module.title}" in The Art of Story! 🎉`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Module Completed!',
          text: text,
          url: window.location.href
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(text);
      alert('Achievement copied to clipboard!');
    }
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md overflow-y-auto"
        onClick={onClose}
      >
        <div className="min-h-full flex items-center justify-center p-4 py-24 relative overflow-hidden">
        {/* Confetti particles */}
        {[...Array(40)].map((_, i) => (
          <motion.div
            key={i}
            initial={{
              x: '50vw',
              y: '50vh',
              opacity: 1,
              scale: 1
            }}
            animate={{
              x: `${Math.random() * 100}vw`,
              y: `${Math.random() * 100}vh`,
              opacity: 0,
              scale: 0,
              rotate: Math.random() * 720
            }}
            transition={{
              duration: 2,
              ease: 'easeOut',
              delay: i * 0.02
            }}
            className="absolute w-2 h-2 rounded-full pointer-events-none"
            style={{
              backgroundColor: ['#FFD700', '#14b8a6', '#a855f7', '#f59e0b', '#ec4899'][i % 5]
            }}
          />
        ))}

        <motion.div
          initial={{ scale: 0.8, y: 50, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.8, y: 50, opacity: 0 }}
          transition={{ type: 'spring', damping: 15 }}
          className="relative max-w-4xl w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute -top-4 -right-4 z-10 w-10 h-10 bg-slate-800 hover:bg-slate-700 rounded-full flex items-center justify-center text-white transition-colors shadow-lg border-2 border-slate-700"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Certificate Card */}
          <div
            ref={certificateRef}
            className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl overflow-hidden border-4 border-double border-amber-500/50"
          >
            {/* Decorative Header */}
            <div className="relative h-32 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 overflow-hidden">
              {/* Animated background pattern */}
              <div className="absolute inset-0 opacity-20">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                  backgroundSize: '40px 40px'
                }} />
              </div>

              {/* Sparkle decorations */}
              <motion.div
                animate={{
                  rotate: 360,
                  scale: [1, 1.2, 1]
                }}
                transition={{
                  rotate: { duration: 20, repeat: Infinity, ease: 'linear' },
                  scale: { duration: 2, repeat: Infinity }
                }}
                className="absolute top-4 left-8"
              >
                <Sparkles className="w-6 h-6 text-yellow-300" />
              </motion.div>
              <motion.div
                animate={{
                  rotate: -360,
                  scale: [1, 1.3, 1]
                }}
                transition={{
                  rotate: { duration: 15, repeat: Infinity, ease: 'linear' },
                  scale: { duration: 3, repeat: Infinity }
                }}
                className="absolute top-6 right-12"
              >
                <Sparkles className="w-5 h-5 text-amber-200" />
              </motion.div>

              {/* Center award icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', delay: 0.2 }}
                  className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-2xl"
                >
                  <Award className="w-12 h-12 text-purple-600" />
                </motion.div>
              </div>
            </div>

            {/* Certificate Content */}
            <div className="px-12 py-10 text-center">
              {/* Title */}
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl font-bold mb-2 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 bg-clip-text text-transparent"
              >
                Module Complete
              </motion.h2>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-slate-400 text-sm mb-8 uppercase tracking-widest"
              >
                The Art of Story
              </motion.p>

              {/* Recipient */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mb-8"
              >
                <p className="text-slate-400 text-sm mb-2">Well done,</p>
                <p className="text-5xl font-bold text-white mb-1" style={{ fontFamily: 'Georgia, serif' }}>
                  {userName}
                </p>
                <div className="w-64 h-0.5 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto mt-3" />
              </motion.div>

              {/* Module Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="mb-8"
              >
                <p className="text-slate-400 text-sm mb-3">you&apos;ve worked through</p>
                <div className="max-w-2xl mx-auto">
                  <h3 className="text-3xl font-bold text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text mb-2">
                    {module.title}
                  </h3>
                  {module.subtitle && (
                    <p className="text-lg text-slate-300 italic">
                      {module.subtitle}
                    </p>
                  )}
                </div>
              </motion.div>

              {/* Achievement Details */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="flex items-center justify-center gap-8 mb-8 text-sm"
              >
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700">
                  <Calendar className="w-4 h-4 text-cyan-400" />
                  <span className="text-slate-300">{completionDate}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700">
                  <BookOpen className="w-4 h-4 text-purple-400" />
                  <span className="text-slate-300">{module.duration || '45 mins'}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-slate-300">{module.difficulty || 'Foundation'}</span>
                </div>
              </motion.div>

              {/* Decorative line: the app's own words, not a quotation, so no quote marks */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="max-w-xl mx-auto mb-6"
              >
                <div className="relative">
                  <div className="absolute -left-4 -top-2 text-6xl text-amber-500/20 font-serif">✦</div>
                  <p className="text-slate-400 italic text-sm leading-relaxed px-6">
                    The stories we tell about our lives are bound up with who we become.
                  </p>
                  <div className="absolute -right-4 -bottom-2 text-6xl text-amber-500/20 font-serif">✦</div>
                </div>
              </motion.div>

              {/* Seal/Badge */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', delay: 0.9 }}
                className="inline-flex items-center justify-center"
              >
                <div className="relative">
                  <div className="w-24 h-24 bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 rounded-full flex items-center justify-center shadow-2xl border-4 border-amber-300/50">
                    <div className="text-center">
                      <Award className="w-10 h-10 text-white mx-auto mb-1" />
                      <p className="text-[8px] text-white font-bold uppercase tracking-wider">Complete</p>
                    </div>
                  </div>
                  {/* Rays emanating from seal */}
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0 -z-10"
                  >
                    {[...Array(12)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute top-1/2 left-1/2 w-0.5 h-8 bg-gradient-to-t from-amber-500/50 to-transparent"
                        style={{
                          transform: `rotate(${i * 30}deg) translateY(-50%)`,
                          transformOrigin: 'top center'
                        }}
                      />
                    ))}
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* Decorative Footer Border */}
            <div className="h-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600" />
          </div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-6"
          >
            <div className="flex items-center gap-4">
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-lg border-2 border-slate-700 hover:border-slate-600 shadow-lg transition-all hover:scale-105"
              >
                <Download className="w-5 h-5" />
                Download
              </button>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-lg border-2 border-slate-700 hover:border-slate-600 shadow-lg transition-all hover:scale-105"
              >
                <Share2 className="w-5 h-5" />
                Share
              </button>
            </div>
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all hover:scale-105"
            >
              Continue
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CompletionCertificate;
